import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { format } from "date-fns";
import {
  useTheme,
  useMediaQuery,
  Box,
  CircularProgress,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { 
  AddCircleOutline, 
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from "hooks/useAuthCheck";
import { LAYOUT_HEIGHT } from "state/constants";
import api from 'state/axios';
import { 
  capitalizeFirstLetter, 
  getSelectedIds, 
  isAnyRowSelected 
} from 'state/helpers';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import DataGridComponent from 'components/DataGridComponent';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from "components/FlexBetween";
import FlexCenter from "components/FlexCenter";
import Header from "components/Header";
import StyledButton from 'components/StyledButton';
import DeleteConfirm from "components/DeleteConfirm";
import CardList from 'components/CardList';


const CostItems = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "Params", "create", false);
  const canEdit = hasRight(permissionMap, "Params", "update", false);
  const canDelete = hasRight(permissionMap, "Params", "delete", false);
    
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [costItems, setCostItems] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: "include",
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  
  useEffect(() => {
    const fetchCostItems = async () => {
      try {
        setLoading(true);
        
        const res = await api.get('/costitems');
        setCostItems(res.data || []);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchCostItems();
  }, []);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      width: 60,
      renderCell: (params) =>
        params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "costItemCode",
      headerName: "CostItemCode",
      width: 200,
    },
    {
      field: "costItemName",
      headerName: "CostItemName",
      width: 300,
    },
    {
      field: "costGroup",
      headerName: "CostGroup",
      width: 90,
      renderCell: (params) => (params.value ? capitalizeFirstLetter(params.value) : ""),
    },    
    {
      field: "category",
      headerName: "Category",
      width: 90,
      renderCell: (params) => (params.value ? capitalizeFirstLetter(params.value) : ""),
    },
    {
      field: "frequency",
      headerName: "Frequency",
      type: "number",
      width: 90,
      renderCell: (params) => (params.value ? capitalizeFirstLetter(params.value) : ""),
    },    
    {
      field: "applicableTo",
      headerName: "ApplicableTo",
      width: 300,
      sortable: false,
      renderCell: (params) => {
        const list = params.row.applicableTo || [];
        if (!list.length) return "";

        return (
          <Box
            sx={{
              height: '100%',
              display: "flex",
              flexDirection: "column",
              overflowY: 'auto',
              overflowX: 'hidden',
              gap: 0.5,
              pr: 1,
            }}
          >
            {list.map((item, index) => (
              <Box
                key={index}
                display="grid"
                gridTemplateColumns="125px 1fr auto"
                alignItems="center"
                columnGap={1}
                
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "primary.main",
                    whiteSpace: "normal",     // cho phép xuống dòng
                    wordBreak: "break-word",  // tự ngắt từ khi cần
                    overflowWrap: "anywhere"  // fallback an toàn
                  }}
                >
                  {Array.isArray(item.jobTitle)
                    ? item.jobTitle.join(", ")
                    : item.jobTitle}
                </Typography>

                <Typography variant="body2" sx={{ textAlign: "right" }}>
                  {new Intl.NumberFormat("vi-VN").format(item.value)}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: "secondary.main",
                    color: "secondary.contrastText",
                    borderRadius: 1,
                    px: 0.5,
                    textAlign: "center",
                    minWidth: 32,
                  }}
                >
                  {item.currency}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      },
    },    
    {
      field: "formula",
      headerName: "Formula",
      width: 350,      
    },
    {
      field: "effectiveFrom",
      headerName: "EffectiveFrom",
      width: 100,
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
    },
    {
      field: "effectiveTo",
      headerName: "EffectiveTo",
      width: 100,
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
    },
    {
      field: "description",
      headerName: "Description",
      width: 450,      
    },
    {
      field: "excludeIfResigned",
      headerName: "ExcludeIfResigned",
      width: 120,
    },
    {
      field: "isContributoryIncome",
      headerName: "IsContributoryIncome",
      width: 120,
    },    
    {
      field: "isNonTaxableIncome",
      headerName: "IsNonTaxableIncome",
      width: 120,
    },   
    {
      field: "sortOrder",
      headerName: "SortOrder",
      width: 90,
      align: "right",
    },
    {
      field: "isActive",
      headerName: "IsActive",
      width: 90,
    },    
  ], []);

  const actionColumn = useMemo(() => {    
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => {
          return (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              sx={{ width: "100%", height: "100%" }}
            >
              {canEdit && (
                <Tooltip title="Edit">
                  <IconButton
                    aria-label="Edit"
                    sx={{
                      color: theme.palette.primary.dark,
                      "&:hover": { color: theme.palette.green.deep },
                    }}
                    onClick={() => navigate(`/costitems/${params.row._id}`)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete">
                  <IconButton
                    aria-label="Delete"
                    sx={{
                      color: theme.palette.primary.dark,
                      "&:hover": { color: theme.palette.green.deep },
                    }}
                    onClick={() => handleDeleteClick(params.row._id)}
                  >
                    <DeleteForever />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ];
  }, [
    navigate, 
    theme.palette, 
    canEdit,
    canDelete,
  ]);
  
  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType("single");
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, costItems);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }

    setDeleteType("bulk");
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === "single") {
        if (!idToDelete) return;
        await api.delete(
          `/costitems/delete/${idToDelete}`
        );
        setCostItems(costItems.filter((item) => item._id !== idToDelete));
        toast.success("Cost item deleted successfully.");
      } else if (deleteType === "bulk") {
        const selectedIds = getSelectedIds(rowSelectionModel, costItems);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/costitems/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setCostItems(costItems.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    costItems,
  ]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Cost Items"
          subtitle={`${loading ? "Loading..." : "List of Cost Items"}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, costItems)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/costitems/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}            
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <Typography color="error">
          Error: {error.message}
        </Typography>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <CardList
                rows={costItems}
                primaryField="costItemCode"
                secondaryField="costItemName"
                fields={[
                  { label: "Cost Group", key: "costGroup" },
                  { label: "Category", key: "category" },
                  { label: "Frequency", key: "frequency" },
                  { label: "Applicable To", key: "applicableTo" },
                  { label: "Effective From", key: "effectiveFrom" },
                  { label: "Effective To", key: "effectiveTo" },
                  { label: "Variable Name", key: "variableName" },
                  { label: "Formula", key: "formula" },
                  { label: "Description", key: "description" },
                  { label: "Exclude If Resigned", key: "excludeIfResigned" },
                  { label: "Is InsurableIncome", key: "isInsurableIncome" },
                  { label: "Is Contributory Income", key: "isContributoryIncome" },
                  { label: "Sort Order", key: "sortOrder" },
                ]}
                isActiveField="isActive"
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/costitems/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={costItems || []}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  onRowSelectionModelChange: setRowSelectionModel,
                  rowSelectionModel,
                  getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                  sx: {    
                    "& .MuiDataGrid-row.isActive-false": {
                      backgroundColor: `${theme.palette.primary.light} !important`,  
                    },
                  },
                }}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default CostItems;